import type { State as AppState } from './app'
import { appReducer as app } from './app'
import type { State as ArtistsState } from './artists'
import { artistsReducer as artists } from './artists'
import type { State as BulkState } from './bulk'
import { bulkReducer as bulk } from './bulk'
import type { BillingProviderComponentsState } from './billing-provider-components'
import { billingProviderComponentsReducer as billingProviderComponents } from './billing-provider-components'
import type { ClientLibrariesState } from './client-libraries'
import { clientLibrariesReducer as clientLibraries } from './client-libraries'
import type { CopyMetadataState } from './copy-metadata'
import { copyMetadataReducer as copyMetadata } from './copy-metadata'
import type { EmailCampaignsState } from './email-campaigns'
import { campaignsReducer as emailCampaigns } from './email-campaigns'
import type { EmailGroupsState } from './emailGroups'
import { emailGroupsReducer as emailGroups } from './emailGroups'
import type { EntitiesState } from './entities'
import { entitiesReducer as entities } from './entities'
import type { State as EventsState } from './events'
import { eventsReducer as events } from './events'
import type { State as ExplorerState } from './explorer'
import { explorerReducer as explorer } from './explorer'
import type { FormsState } from './forms'
import { formsReducer as forms } from './forms'
import type { State as GroupsState } from './groups'
import { groupsReducer as groups } from './groups'
import type { State as MediaState } from './media'
import { mediaReducer as media } from './media'
import type { NestingState } from './nesting'
import { nestingReducer as nesting } from './nesting'
import type { NotificationState } from './notifications'
import { notificationsReducer as notifications } from './notifications'
import type { State as PlaylistsState } from './playlists'
import { playlistsReducer as playlists } from './playlists'
import type { State as PlaylistsCuratedState } from './playlists-curated'
import { playlistsCuratedReducer as playlistsCurated } from './playlists-curated'
import type { State as ReferralInvitesState } from './referralInvites'
import { referralInvitesReducer as referralInvites } from './referralInvites'
import type { ReportsState } from './reports'
import { reportsReducer as reports } from './reports'
import type { State as SearchState } from './search'
import { searchReducer as search } from './search'
import type { SearchPageState } from './search-page'
import searchPage from './search-page'
import type { State as SeatsAndTracksState } from './seats-and-tracks'
import { seatsAndTracksReducer as seatsAndTracks } from './seats-and-tracks'
import type { State as SelectionState } from './selection'
import { selectionReducer as selection } from './selection'
import type { ServerState } from './server'
import { serverReducer as server } from './server'
import type { State as ShortlistsState } from './shortlists'
import { shortlistsReducer as shortlists } from './shortlists'
import type { State as StatsState } from './stats'
import { statsReducer as stats } from './stats'
import type { State as SubscriptionState } from './subscription'
import { subscriptionReducer as subscription } from './subscription'
import type { State as ThemesState } from './themes'
import { themesReducer as themes } from './themes'
import type { State as TracksState } from './tracks'
import { tracksReducer as tracks } from './tracks'
import type { TrackUploaderState } from './trackUploader'
import { trackUploaderReducer as trackUploader } from './trackUploader'
import type { State as UIElementsState } from './uiElements'
import { uiElementsReducer as uiElements } from './uiElements'
import type { State as UploadState } from './upload'
import { uploadReducer as upload } from './upload'
import type { UserReducer } from './user'
import { userReducer as user } from './user'
import type { State as WatermarkingState } from './watermarking'
import { watermarkingReducer as watermarking } from './watermarking'
import type { RightsManagementState } from './rightsManagement'
import { rightsManagementReducer as rightsManagement } from './rightsManagement'
import type { AddToDiscoState } from './public-playlists/add-to-disco'
// discoAdd is used by frontend for Save to DISCO from discover page.
// Mobile app explicitly imports individual reducers and won't include this.
import { addToDiscoReducer as discoAdd } from './public-playlists/add-to-disco'

// State type for frontend app. Mobile app builds its own store type and doesn't import this.
export type State = {
  app: AppState
  media: MediaState
  explorer: ExplorerState
  shortlists: ShortlistsState
  server: ServerState
  bulk: BulkState
  user: UserReducer
  search: SearchState
  searchPage: SearchPageState
  notifications: NotificationState
  themes: ThemesState
  groups: GroupsState
  emailGroups: EmailGroupsState
  playlists: PlaylistsState
  playlistsCurated: PlaylistsCuratedState
  tracks: TracksState
  upload: UploadState
  entities: EntitiesState
  forms: FormsState
  reports: ReportsState
  stats: StatsState
  events: EventsState
  uiElements: UIElementsState
  selection: SelectionState
  trackUploader: TrackUploaderState
  referralInvites: ReferralInvitesState
  emailCampaigns: EmailCampaignsState
  clientLibraries: ClientLibrariesState
  artists: ArtistsState
  subscription: SubscriptionState
  watermarking: WatermarkingState
  billingProviderComponents: BillingProviderComponentsState
  seatsAndTracks: SeatsAndTracksState
  copyMetadata: CopyMetadataState
  nesting: NestingState
  rightsManagement: RightsManagementState
  discoAdd: AddToDiscoState
  routing: any
}

export const reducers = {
  app,
  media,
  explorer,
  shortlists,
  server,
  bulk,
  user,
  search,
  searchPage,
  notifications,
  themes,
  groups,
  emailGroups,
  playlists,
  playlistsCurated,
  tracks,
  upload,
  entities,
  forms,
  reports,
  stats,
  events,
  uiElements,
  selection,
  trackUploader,
  referralInvites,
  emailCampaigns,
  clientLibraries,
  artists,
  subscription,
  watermarking,
  billingProviderComponents,
  seatsAndTracks,
  copyMetadata,
  nesting,
  rightsManagement,
  discoAdd,
}
